import { AddPill, TypePill, type MeetingState } from '@wundo/ui';
import type { CalendarMeetingItem } from '@/lib/meetings.ts';
import { MeetingKindPill } from './MeetingKindPill.tsx';
import { m } from '@/paraglide/messages.js';

export interface AgendaMeetingPillProps {
  meeting: CalendarMeetingItem;
  state: MeetingState;
}

/**
 * The pill slot of an agenda card carries the state: the offer while a meeting
 * is not in Wundo — the journey step Wundo suggests for it, else a plain add —
 * and what it is once it is. A meeting linked to a journey step is
 * labelled by its journey instead of its kind — its gesprekstype comes from the
 * step, so two pills would say the same thing — with the step as "3/5", or
 * three starters' meetings would look alike.
 */
export function AgendaMeetingPill({ meeting, state }: AgendaMeetingPillProps) {
  if (state === 'candidate') {
    const suggestion = meeting.journey_suggestion;
    if (!suggestion) return <AddPill>{m.agenda_add_pill()}</AddPill>;
    const offer = m.agenda_journey_suggestion_pill({
      name: suggestion.type_name,
      position: suggestion.step_position,
    });
    const short = m.agenda_journey_suggestion_pill_short({
      position: suggestion.step_position,
    });
    return (
      <AddPill variant='journey' title={offer} short={short}>
        {offer}
      </AddPill>
    );
  }
  if (state !== 'tracked') return null;

  if (meeting.journey) {
    const {
      type_name: name,
      step_position: position,
      step_count: count,
    } = meeting.journey;
    const label = m.agenda_journey_pill({ name, position, count });
    /* Where the week column has no room for the journey's name, the pill keeps the step and hovers in full. */
    return (
      <TypePill
        type='journey'
        size='sm'
        title={label}
        short={m.agenda_journey_pill_short({ position, count })}
      >
        {label}
      </TypePill>
    );
  }
  return <MeetingKindPill kind={meeting.kind} />;
}
